import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle, TrendingUp, Users, DollarSign } from "lucide-react";
import { getPostcodeBySuburb, getSuburbSuggestions } from "@/data/australianSuburbs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TradieSignup() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [suburbSuggestions, setSuburbSuggestions] = useState([]);
  const [showSuburbSuggestions, setShowSuburbSuggestions] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    suburb: '',
    postcode: '',
    state: '',
    description: '',
    abn: '',
    business_hours: '',
    available_24_7: false
  });

  const australianStates = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NT', label: 'Northern Territory' }
  ];

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.business_name || !formData.contact_name || !formData.email || 
        !formData.phone || !formData.postcode || !formData.state || 
        selectedServices.length === 0) {
      toast.error("Please fill in all required fields and select at least one service");
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        services: selectedServices
      };
      await axios.post(`${API}/tradies`, submitData);
      toast.success("Registration successful! Welcome to LocalTradiesOnline.");
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error("Error registering tradie:", error);
      const errorMsg = error.response?.data?.detail || "Failed to register. Please try again.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Handle suburb input for autocomplete
    if (field === 'suburb') {
      if (value.length >= 2) {
        const suggestions = getSuburbSuggestions(value);
        setSuburbSuggestions(suggestions);
        setShowSuburbSuggestions(suggestions.length > 0);
      } else {
        setSuburbSuggestions([]);
        setShowSuburbSuggestions(false);
      }
    }
  };

  const handleSuburbSelect = (suburb, postcode, state) => {
    console.log('Setting state to:', state); // Debug log
    setFormData({ 
      ...formData, 
      suburb: suburb,
      postcode: postcode,
      state: state
    });
    setShowSuburbSuggestions(false);
    setSuburbSuggestions([]);
  };

  const toggleService = (serviceSlug) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceSlug)) {
        return prev.filter(s => s !== serviceSlug);
      } else {
        return [...prev, serviceSlug];
      }
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 
              className="text-4xl md:text-6xl font-bold tracking-tight text-[#0f172a] mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="signup-heading"
            >
              Grow Your <span className="text-[#f97316]">Trade Business</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Join thousands of tradies connecting with customers across Australia
            </p>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-[#f97316]" />
              </div>
              <h3 className="font-bold text-lg text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                More Customers
              </h3>
              <p className="text-sm text-slate-600">
                Get matched with customers actively looking for your services
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-6 h-6 text-[#f97316]" />
              </div>
              <h3 className="font-bold text-lg text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Grow Your Reputation
              </h3>
              <p className="text-sm text-slate-600">
                Build trust with verified reviews and ratings
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-6 h-6 text-[#f97316]" />
              </div>
              <h3 className="font-bold text-lg text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Flexible Pricing
              </h3>
              <p className="text-sm text-slate-600">
                Pay per lead or subscribe - choose what works for you
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-8 md:p-12" data-testid="signup-form">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Business Information */}
              <div>
                <h2 
                  className="text-2xl md:text-3xl font-semibold text-[#0f172a] mb-6"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Business Information
                </h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="business_name" className="text-[#0f172a] font-semibold mb-2">Business Name *</Label>
                      <Input
                        id="business_name"
                        type="text"
                        value={formData.business_name}
                        onChange={(e) => handleChange('business_name', e.target.value)}
                        className="h-12"
                        placeholder="Smith Electrical Services"
                        data-testid="business-name-input"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact_name" className="text-[#0f172a] font-semibold mb-2">Contact Name *</Label>
                      <Input
                        id="contact_name"
                        type="text"
                        value={formData.contact_name}
                        onChange={(e) => handleChange('contact_name', e.target.value)}
                        className="h-12"
                        placeholder="John Smith"
                        data-testid="contact-name-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-[#0f172a] font-semibold mb-2">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="h-12"
                        placeholder="john@smithelectrical.com.au"
                        data-testid="email-input"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-[#0f172a] font-semibold mb-2">Phone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        className="h-12"
                        placeholder="04XX XXX XXX"
                        data-testid="phone-input"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="abn" className="text-[#0f172a] font-semibold mb-2">ABN (Optional)</Label>
                    <Input
                      id="abn"
                      type="text"
                      value={formData.abn}
                      onChange={(e) => handleChange('abn', e.target.value)}
                      className="h-12"
                      placeholder="12 345 678 901"
                      data-testid="abn-input"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <h2 
                  className="text-2xl md:text-3xl font-semibold text-[#0f172a] mb-6"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Service Area
                </h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="relative">
                      <Label htmlFor="suburb" className="text-[#0f172a] font-semibold mb-2">Suburb</Label>
                      <Input
                        id="suburb"
                        type="text"
                        value={formData.suburb}
                        onChange={(e) => handleChange('suburb', e.target.value)}
                        onBlur={() => setTimeout(() => setShowSuburbSuggestions(false), 200)}
                        onFocus={() => formData.suburb.length >= 2 && suburbSuggestions.length > 0 && setShowSuburbSuggestions(true)}
                        className="h-12"
                        placeholder="Start typing suburb..."
                        data-testid="suburb-input"
                      />
                      {showSuburbSuggestions && suburbSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                          {suburbSuggestions.map((item, index) => (
                            <div
                              key={index}
                              className="px-4 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                              onMouseDown={() => handleSuburbSelect(item.suburb, item.postcode, item.state)}
                            >
                              <div className="font-medium">{item.suburb}, {item.state}</div>
                              <div className="text-xs text-slate-500">Postcode: {item.postcode}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="postcode" className="text-[#0f172a] font-semibold mb-2">Postcode *</Label>
                      <Input
                        id="postcode"
                        type="text"
                        value={formData.postcode}
                        onChange={(e) => handleChange('postcode', e.target.value)}
                        className="h-12"
                        placeholder="2000"
                        data-testid="postcode-input"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="state" className="text-[#0f172a] font-semibold mb-2">State *</Label>
                      <select
                        id="state"
                        value={formData.state}
                        onChange={(e) => handleChange('state', e.target.value)}
                        className="h-12 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#f97316]"
                        data-testid="state-select"
                        required
                      >
                        <option value="">Select State</option>
                        {australianStates.map((state) => (
                          <option key={state.value} value={state.value}>
                            {state.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Services */}
              <div>
                <h2 
                  className="text-2xl md:text-3xl font-semibold text-[#0f172a] mb-6"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Services You Offer *
                </h2>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="services-checkboxes">
                  {services.map((service) => (
                    <div 
                      key={service.id} 
                      className="flex items-center space-x-2 p-3 rounded-lg border border-slate-200 hover:border-[#f97316] transition-colors"
                    >
                      <Checkbox 
                        id={service.slug}
                        checked={selectedServices.includes(service.slug)}
                        onCheckedChange={() => toggleService(service.slug)}
                        data-testid={`service-checkbox-${service.slug}`}
                      />
                      <Label 
                        htmlFor={service.slug} 
                        className="text-sm cursor-pointer flex-1"
                        onClick={() => toggleService(service.slug)}
                      >
                        {service.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <h2 
                  className="text-2xl md:text-3xl font-semibold text-[#0f172a] mb-6"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  About Your Business
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="description" className="text-[#0f172a] font-semibold mb-2">
                      Tell customers about your business, experience, and what makes you stand out
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      className="min-h-32"
                      placeholder="We are a family-owned business with over 20 years of experience..."
                      data-testid="description-textarea"
                    />
                  </div>

                  <div>
                    <Label htmlFor="business_hours" className="text-[#0f172a] font-semibold mb-2">
                      Business Hours
                    </Label>
                    <Input
                      id="business_hours"
                      type="text"
                      value={formData.business_hours}
                      onChange={(e) => handleChange('business_hours', e.target.value)}
                      className="h-12"
                      placeholder="e.g., Mon-Fri: 8am-6pm, Sat: 9am-2pm"
                      data-testid="business-hours-input"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="available_24_7"
                      checked={formData.available_24_7}
                      onCheckedChange={(checked) => handleChange('available_24_7', checked)}
                      data-testid="available-24-7-checkbox"
                    />
                    <Label 
                      htmlFor="available_24_7" 
                      className="text-sm font-medium cursor-pointer"
                    >
                      Available 24/7 (Emergency callouts)
                    </Label>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 bg-[#f97316] hover:bg-[#ea580c] text-white font-bold text-lg"
                disabled={loading}
                data-testid="submit-signup-btn"
              >
                {loading ? 'Submitting...' : 'Join LocalTradiesOnline'}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                By registering, you agree to our Terms of Service and Privacy Policy
              </p>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}